/* Shared helpers for the Season filter that lives in every date-bearing
   module. See "REGLA DE FILTRO DE SEASON" in CLAUDE.md for the rule.

   Conventions:
     - Filter values are season **IDs** (stringified). Earlier versions
       matched by display name, but two independent name-computation
       paths (the helper's vs each module's seasonNameFor*) could drift
       on edge cases (missing endEstimate, manually-set seasonName,
       data shape quirks from imports), so a season's records would
       silently drop out of the filtered set even though the season's
       checkbox was visibly selected. Matching by seasonId removes that
       entire class of bugs because the ID is the season's primary key.
     - Each option's display LABEL is still the human-readable name
       ("2025-26"), only the VALUE has changed.
     - Open status = "open" | "reOpened" | "preClose" (all three are
       operationally active; "closed" is the only true off state).
     - Default filter state on mount = the IDs of all Open seasons.
     - Empty filter array means "no restriction" (show all).
*/

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }

function genSeasonName(start, end) {
  if (!start) return "";
  if (!end) return String(start).substring(0, 4);
  return String(start).substring(0, 4) + "-" + String(end).substring(2, 4);
}

export function isSeasonOpen(status) {
  return status === "open" || status === "reOpened" || status === "preClose";
}

export function loadSeasons() { return loadLS("gfi-seasons", []); }

/* IDs of all currently-Open seasons. Use this to seed useState in the
   consumer module so the first render only shows data from Open seasons. */
export function defaultOpenSeasonIds() {
  return loadSeasons()
    .filter(function (s) { return isSeasonOpen(s.status); })
    .map(function (s) { return s.id != null ? String(s.id) : ""; })
    .filter(Boolean);
}

/* SearchSelect `groups` payload. Value = seasonId (string), label =
   display name. Open group first (matches what defaultOpenSeasonIds
   pre-selects), Closed group second. Sorted by display name desc within
   each group so the newest season appears at the top. */
export function buildSeasonFilterOptions(lang) {
  var open = []; var closed = [];
  loadSeasons().forEach(function (s) {
    if (s.id == null) return;
    var name = s.seasonName || genSeasonName(s.startDate, s.endEstimate);
    if (!name) return;
    var opt = { value: String(s.id), label: name };
    if (isSeasonOpen(s.status)) open.push(opt); else closed.push(opt);
  });
  function byLabelDesc(a, b) { return (b.label || "").localeCompare(a.label || ""); }
  open.sort(byLabelDesc); closed.sort(byLabelDesc);
  return [
    { header: lang === "es" ? "Abiertas" : "Open", options: open },
    { header: lang === "es" ? "Cerradas" : "Closed", options: closed },
  ];
}

export function seasonFilterPlaceholder(lang) {
  return lang === "es" ? "Todas las temporadas" : "All seasons";
}

/* Resolve a record's effective seasonId given its date and (possibly
   stale or missing) stored seasonId. Prefers the stored id when it
   points to a real season, falls back to date-range lookup against the
   provided seasons array, returns "" when neither resolves. Pass the
   `seasons` array (loaded once per render via loadSeasons or useMemo)
   to avoid hitting localStorage on every record. */
export function resolveSeasonId(seasons, dateStr, sid) {
  if (sid != null && sid !== "") {
    var byId = (seasons || []).find(function (s) { return String(s.id) === String(sid); });
    if (byId) return String(byId.id);
  }
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  var s = (seasons || []).find(function (s) {
    if (!s.startDate || !s.endEstimate) return false;
    return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00");
  });
  return s ? String(s.id) : "";
}
